/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useHistory } from "react-router-dom";
import { Cell } from "react-table";
import { Button } from "@nordcloud/gnui";
import { ACTION, PopoverControls, IdCell } from "~/components";
import { CustomCell } from "~/components/Tables/Cells";
import { ROUTES } from "~/routing/routes";
import { isNotEmpty, isString } from "~/tools";
import { AccountStatusIcon } from "~/views/accounts/AccountStatusIcon";
import AccountTypeIcon from "~/views/accounts/AccountTypeIcon";
import { ACCOUNT_TYPE_LIST } from "./constants";
import { Account } from "./types";
import { getAccountUrl, isAccountDiscovered } from "./utils";

type Item = {
  label: string;
  action: ACTION;
  onClick: () => void;
};

export type UserShorthand = {
  id: string;
  name: string;
};

type AccountsListProps = {
  onRemoveHandler: (id: string, name: string) => void;
  activeTab: number;
  // remove when separate columns for cloud accounts are created
  showAccountsV2?: boolean;
};

const getIdColumnDetail = (activeTab: number) =>
  ACCOUNT_TYPE_LIST[activeTab].idColumn
    ? [
        {
          Header: ACCOUNT_TYPE_LIST[activeTab].idColumn?.label,
          accessor: "accountid",
          disableSortBy: true,
          Cell: ({ row }: Cell<Account>) => {
            const getValueFn = ACCOUNT_TYPE_LIST[activeTab].idColumn?.getValue;
            const value = getValueFn
              ? getValueFn(row.original)
              : row.original.providerId;
            return isString(value) && isNotEmpty(value) ? (
              <CustomCell copy name={value} />
            ) : null;
          },
        },
      ]
    : [];

export const AccountsListColumns = ({
  onRemoveHandler,
  activeTab,
  showAccountsV2,
}: AccountsListProps) => [
  {
    accessor: "activated",
    width: "3%",
    disableSortBy: true,
    Cell: ({
      row: {
        original: { activated, status, provider, providerType },
      },
    }: Cell<Account>) => {
      return (
        <div css={{ position: "relative" }}>
          <AccountStatusIcon
            provider={provider}
            providerType={providerType}
            activated={activated}
            status={status}
          />
        </div>
      );
    },
  },
  {
    accessor: "provider",
    disableSortBy: true,
    width: "3%",
    Cell: ({
      row: {
        original: { provider, providerType },
      },
    }: Cell<Account>) => {
      return (
        <AccountTypeIcon provider={provider} providerType={providerType} />
      );
    },
  },
  {
    Header: "Account Name / Description",
    accessor: "name",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => (
      <IdCell
        id={row.original.id}
        name={row.original.displayName ?? row.original.name}
        provider={row.original.provider}
        providerId={row.original.providerId}
        description={row.original.description}
        url={getAccountUrl(row.original.providerId, row.original.provider)}
        type={showAccountsV2 ? "cloud-accounts" : "accounts"}
      />
    ),
  },
  ...getIdColumnDetail(activeTab),
  {
    Header: "Account Owner / Contact Person",
    accessor: "owner.name/contactPerson.name",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => {
      const contactList =
        row?.original?.contacts
          ?.map((item) => {
            return item.name;
          })
          .join(" | ") || "";
      return (
        <CustomCell
          name={row?.original?.owner?.name || ""}
          description={contactList}
        />
      );
    },
  },
  {
    Header: "",
    accessor: "actions",
    disableSortBy: true,
    width: "3rem",
    hasLeftBorder: false,
    Cell: ({ row }: Cell<Account>) => {
      const history = useHistory();
      const DeleteAction: Item = {
        label: "Delete Account",
        action: ACTION.DELETE,
        onClick: () => {
          onRemoveHandler(row?.original?.id, row?.original?.name);
        },
      };
      const ProvideAction: Item = {
        label: "Onboard Account",
        action: ACTION.EDIT,
        onClick: () => {
          history.push(
            `${ROUTES.accounts.add}?accountId=${row?.original?.id}&source=${activeTab}`
          );
        },
      };
      const ActionItems = () => {
        if (
          isAccountDiscovered(
            row?.original?.providerType || "",
            row?.original?.status || ""
          )
        ) {
          return [ProvideAction, DeleteAction];
        } else {
          return [DeleteAction];
        }
      };

      return (
        <PopoverControls
          items={ActionItems()}
          trigger={
            <Button
              severity="low"
              icon="menu"
              size="md"
              data-testid="orgunit-popover-trigger"
            />
          }
        />
      );
    },
  },
];
